import { requestInstance } from './AuthService/AccessTokenUpdateService';
import { BASE_URL } from '../api/constants';
import { USER_TYPES } from '../context/UserTypeContext';
import Fuse from 'fuse.js';

// Cache configuration
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const searchCache = {
  products: new Map(),
  buyers: new Map(),
};

/**
 * Generate a cache key from search parameters
 * @param {string} query - Search query
 * @param {Object} filters - Search filters
 * @returns {string} - Cache key
 */
const generateCacheKey = (query = '', filters = {}) => {
  return `${query}:${JSON.stringify(filters)}`;
};

/**
 * Check if a cached result is still valid
 * @param {Object} cachedData - The cached data with timestamp
 * @returns {boolean} - True if cache is valid, false otherwise
 */
const isCacheValid = (cachedData) => {
  if (!cachedData || !cachedData.timestamp) return false;
  const now = new Date().getTime();
  return (now - cachedData.timestamp) < CACHE_EXPIRY_TIME;
};

/**
 * Add result to cache
 * @param {string} cacheType - Type of cache ('products' or 'buyers')
 * @param {string} key - Cache key
 * @param {Object} data - Data to cache
 */
const addToCache = (cacheType, key, data) => {
  if (!searchCache[cacheType]) return;
  
  searchCache[cacheType].set(key, {
    data,
    timestamp: new Date().getTime()
  });
  
  // Clean up old cache entries if the cache is getting too large
  if (searchCache[cacheType].size > 50) {
    // Find the oldest entry
    let oldestKey = null;
    let oldestTime = Infinity;
    
    searchCache[cacheType].forEach((value, key) => {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp;
        oldestKey = key;
      }
    });
    
    // Remove the oldest entry
    if (oldestKey) {
      searchCache[cacheType].delete(oldestKey);
    }
  }
};

/**
 * Get cached search result
 * @param {string} cacheType - Type of cache ('products' or 'buyers')
 * @param {string} key - Cache key
 * @returns {Object|null} - Cached data or null if not found or expired
 */
const getFromCache = (cacheType, key) => {
  if (!searchCache[cacheType]) return null;
  
  const cachedData = searchCache[cacheType].get(key);
  
  if (cachedData && isCacheValid(cachedData)) {
    console.log(`Using cached ${cacheType} results for`, key);
    return cachedData.data;
  }
  
  // Remove expired cache entry
  if (cachedData) {
    searchCache[cacheType].delete(key);
  }
  
  return null;
};

/**
 * Clear entire search cache or specific cache type
 * @param {string} cacheType - Optional cache type to clear ('products' or 'buyers')
 */
export const clearSearchCache = (cacheType) => {
  if (cacheType) {
    if (searchCache[cacheType]) {
      searchCache[cacheType].clear();
    }
  } else {
    Object.keys(searchCache).forEach(type => {
      searchCache[type].clear();
    });
  }
};

/**
 * Clears the search cache
 * @param {string} type - Optional type of cache to clear ('products' or 'buyers')
 */
export const clearCache = (type) => {
  if (!type) {
    // Clear all caches
    searchCache.products.clear();
    searchCache.buyers.clear();
    console.log('Cleared all search caches');
  } else if (type === 'products') {
    searchCache.products.clear();
    console.log('Cleared products search cache');
  } else if (type === 'buyers') {
    searchCache.buyers.clear();
    console.log('Cleared buyers search cache');
  }
};

/**
 * Applies fuzzy search to a list of items using Fuse.js
 * @param {Array} items - The array of items to search within
 * @param {string} query - The search query
 * @param {Object} options - Additional options for the fuzzy search
 * @returns {Array} - The filtered array of items that match the query
 */
const applyFuzzySearch = (items, query, options = {}) => {
  if (!items || !items.length || !query) {
    return items;
  }

  // Default options for Fuse.js
  const defaultOptions = {
    // Include both exact and fuzzy matching
    includeScore: true,
    // Lower threshold means stricter matching (0.0 = exact, 1.0 = anything)
    threshold: 0.3,
    // How many characters need to match (0.0 = exact, 1.0 = anything)
    location: 0,
    // How far to search for a match (higher = more fuzzy)
    distance: 100,
    // Determines approximately how close the match must be to the fuzzy location
    minMatchCharLength: 2,
    // Whether to use AND or OR between search terms
    useExtendedSearch: true,
    // Whether to sort by score (true) or by index order (false)
    shouldSort: true,
    // Fields to search in, with weights
    keys: [
      // Product fields
      { name: 'name', weight: 1.0 },
      { name: 'description', weight: 0.7 },
      { name: 'category.name', weight: 0.8 },
      // Include subcategories (array of objects)
      { name: 'subcategories.name', weight: 0.7 },
      // Include tags (array of strings or objects)
      { name: 'tags', weight: 0.6 },
      { name: 'tags.name', weight: 0.6 },
      // Company fields
      { name: 'company.brand_name', weight: 0.9 },
      
      // Buyer-specific fields (for when searching buyers)
      { name: 'first_name', weight: 0.9 },
      { name: 'last_name', weight: 0.9 },
      { name: 'company.country', weight: 0.7 },
      { name: 'interests', weight: 0.6 }
    ],
    // Enable tokenization for better partial matching
    tokenize: true,
    // Match all tokens (if tokenize is true)
    matchAllTokens: false,
    // Ignore location for better matching across fields
    ignoreLocation: true,
    // Find all matches, not just the first one
    findAllMatches: true
  };

  // Adjust search options based on query complexity
  const words = query.trim().split(/\s+/);
  if (words.length > 1) {
    // For multi-word queries, be more lenient
    defaultOptions.threshold = 0.4;
    defaultOptions.matchAllTokens = false; // Allow partial matches for multi-word queries
  } else {
    // For single-word queries, be more strict
    defaultOptions.threshold = 0.3;
    defaultOptions.matchAllTokens = true; // Require all tokens to match for single-word queries
  }

  // Merge default options with any provided options
  const fuseOptions = { ...defaultOptions, ...options };
  
  // Create a new Fuse instance with the items and options
  const fuse = new Fuse(items, fuseOptions);
  
  // Perform the search
  const results = fuse.search(query);
  
  // Log search details for debugging
  console.log(`Fuzzy search for "${query}" found ${results.length} matches`);
  
  // Return just the items (not the score and indices that Fuse.js includes)
  return results.map(result => result.item);
};

/**
 * Search for products with optional filtering
 * @param {string} query - Search query
 * @param {Object} filters - Additional filters (category, location, etc.)
 * @param {boolean} useCache - Whether to use cached results if available
 * @param {boolean} useFuzzy - Whether to apply fuzzy search to results
 * @returns {Promise} - Promise with search results
 */
export const searchProducts = async (query, filters = {}, useCache = true, useFuzzy = true) => {
  try {
    // Check cache first if caching is enabled
    if (useCache) {
      const cacheKey = generateCacheKey(query, filters);
      const cachedResult = getFromCache('products', cacheKey);
      
      if (cachedResult) {
        return cachedResult;
      }
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    if (query) params.append('name', query);
    
    // Add filters - handle category ID or 'all' special case
    if (filters.category) {
      if (filters.category !== 'all') {
        // If it's a numeric ID, use it directly
        if (!isNaN(filters.category)) {
          params.append('category', filters.category);
        } else {
          // If it's a name, pass it as-is (the backend expects category__name)
          params.append('category', filters.category);
        }
      }
      // If 'all' is selected, don't add any category filter to get all categories
    }
    
    // Temporarily only enable location filtering for simple searches
    // to avoid complex filtersets that might cause 500 errors
    if (filters.location && !filters.category) {
      params.append('countries', filters.location);
    }
    
    console.log('Searching products with params:', params.toString());
    
    // Remove duplicate api/ prefix - BASE_URL already includes it
    const url = `${BASE_URL}products/?${params.toString()}`;
    console.log('Request URL:', url);
    
    const response = await requestInstance.get(url);
    console.log('Search API response status:', response.status);
    
    // Ensure we're handling the response correctly even if empty
    if (!response || !response.data) {
      console.warn('Empty response received from product search');
      return { results: [], count: 0 };
    }
    
    // Debug log the actual response structure to help diagnose issues
    console.log('Response structure:', {
      hasResults: !!response.data.results,
      resultsCount: response.data.results ? response.data.results.length : 0,
      hasCount: 'count' in response.data,
      count: response.data.count
    });
    
    // Apply fuzzy search if enabled and we have a query
    let results = response.data.results || [];
    if (useFuzzy && query && query.trim() !== '') {
      results = applyFuzzySearch(results, query, {
        keys: ['name', 'description', 'category.name', 'company.brand_name', 'subcategories.name', 'tags'],
        threshold: 0.3 // Slightly stricter matching for products
      });
      
      // Update the response data with fuzzy search results
      response.data = {
        ...response.data,
        results,
        count: results.length
      };
    }
    
    // Cache the results if caching is enabled
    if (useCache) {
      const cacheKey = generateCacheKey(query, filters);
      addToCache('products', cacheKey, response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error searching products:', error);
    console.error('Error details:', error.response ? error.response.data : 'No response data');
    // Return an empty result structure so UI can handle it gracefully
    return { results: [], count: 0 };
  }
};

/**
 * Search for buyers with optional filtering
 * @param {string} query - Search query
 * @param {Object} filters - Additional filters (location, etc.)
 * @param {boolean} useCache - Whether to use cached results if available
 * @param {boolean} useFuzzy - Whether to apply fuzzy search to results
 * @returns {Promise} - Promise with search results
 */
export const searchBuyers = async (query, filters = {}, useCache = true, useFuzzy = true) => {
  try {
    // Check cache first if caching is enabled
    if (useCache) {
      const cacheKey = generateCacheKey(query, filters);
      const cachedResult = getFromCache('buyers', cacheKey);
      
      if (cachedResult) {
        return cachedResult;
      }
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    if (query) params.append('search', query);
    
    // Add filters - for buyer search, the backend expects 'location' parameter
    // This maps to the country filter in the backend API
    if (filters.location) params.append('location', filters.location);
    
    console.log('Searching buyers with params:', params.toString());
    
    // Remove duplicate api/ prefix - BASE_URL already includes it
    const url = `${BASE_URL}company/buyers/?${params.toString()}`;
    console.log('Request URL:', url);
    
    const response = await requestInstance.get(url);
    console.log('Buyer search API response:', response);
    
    // Apply fuzzy search if enabled and we have a query
    let results = response.data?.results || [];
    if (useFuzzy && query && query.trim() !== '' && results.length > 0) {
      results = applyFuzzySearch(results, query, {
        keys: ['brand_name', 'first_name', 'last_name', 'country', 'primary_interests', 'subcategories.name', 'tags'],
        threshold: 0.4 // More lenient matching for buyers
      });
      
      // Update the response data with fuzzy search results
      if (response.data) {
        response.data = {
          ...response.data,
          results,
          count: results.length
        };
      }
    }
    
    // Cache the results if caching is enabled
    if (useCache && response && response.data) {
      const cacheKey = generateCacheKey(query, filters);
      addToCache('buyers', cacheKey, response.data);
    }
    
    // Return just the data, not the full response object
    // This makes the return format consistent between both search functions
    return response.data || { results: [], count: 0 };
  } catch (error) {
    console.error('Error searching buyers:', error);
    console.error('Error details:', error.response ? error.response.data : 'No response data');
    // Return an empty result structure so UI can handle it gracefully
    return { results: [], count: 0 };
  }
};

/**
 * Get search results based on user type
 * @param {string} userType - Type of user (brand, buyer, unregistered) or USER_TYPES constant
 * @param {string} query - Search query
 * @param {Object} filters - Additional filters
 * @param {boolean} useCache - Whether to use cached results if available
 * @param {boolean} useFuzzy - Whether to apply fuzzy search to results
 * @returns {Promise} - Promise with search results
 */
export const getSearchResults = async (userType, query, filters = {}, useCache = true, useFuzzy = true) => {
  try {
    console.log(`Getting search results for userType: ${userType}, query: ${query}, filters:`, filters);
    
    // Handle both string constants and USER_TYPES constants
    const isBrandUser = userType === USER_TYPES.BRAND || userType === 'brand';
    
    if (isBrandUser) {
      return await searchBuyers(query, filters, useCache, useFuzzy);
    } else {
      return await searchProducts(query, filters, useCache, useFuzzy);
    }
  } catch (error) {
    console.error('Error in getSearchResults:', error);
    return { results: [], count: 0 };
  }
};

// Add function to fetch buyer countries
export const getBuyerCountries = async () => {
  try {
    // Remove duplicate api/ prefix - BASE_URL already includes it
    const url = `${BASE_URL}company/buyer-countries/`;
    const response = await requestInstance.get(url);
    return response.data || []; // Should be an array of country strings
  } catch (error) {
    console.error('Error fetching buyer countries:', error);
    // Return empty array for error handling
    return []; 
  }
};

/**
 * Fetches the list of categories that currently have products associated with them.
 * @returns {Promise<Array>} - Promise resolving to an array of category objects or empty array on error.
 */
export const getAvailableCategories = async () => {
  try {
    const url = `${BASE_URL}products/categories/?with_products=true`;
    console.log('Fetching available categories from:', url);
    const response = await requestInstance.get(url);

    // The categories are expected in the 'results' array of the response data
    if (response && response.data && response.data.results) {
      console.log('Available categories received:', response.data.results);
      return response.data.results;
    } else {
      console.warn('No results found when fetching available categories', response);
      return [];
    }
  } catch (error) {
    console.error('Error fetching available categories:', error);
    console.error('Error details:', error.response ? error.response.data : 'No response data');
    return []; // Return empty array for graceful error handling in UI
  }
};
